// Custom React Flow node for structural containers (Account, VPC, Subnet,
// Tenant, Subscription, Resource Group, Organization, Folder, Project...).
//
// Phase 4+5 additions:
// - HTML5 drop target (parent for new resources / move target for existing nodes)
// - Right-click context menu trigger
// - Visual states: validDropTarget, dropHovering, pendingPlacementValid,
//   peerSource, peerTargetCandidate.

import { Handle, Position } from "@xyflow/react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";
import { getStructuralMeta } from "../../lib/archModel";
import { addressLabel } from "../../lib/diagramTabs";

export default function StructuralNode({ id, data }) {
  const meta = getStructuralMeta(data.type);
  const isCollapsed = !!data.collapsed;
  const [hover, setHover] = useState(false);

  // Address space is drawn only where it is the subject (the Network tab). On the
  // Cloud tab a CIDR under every container is noise competing with the services.
  const address = data.showAddressing ? addressLabel({ config: data.config }) : "";
  const visibility = data.showAddressing ? data.config?.visibility : null;

  // Compose the border based on interaction state.
  let borderColor = `color-mix(in oklab, ${meta.color} 35%, var(--color-border-primary))`;
  let outline = "none";
  if (data.invalid) {
    borderColor = "var(--color-danger, #ef4444)";
    outline = "2px solid color-mix(in oklab, var(--color-danger, #ef4444) 85%, transparent)";
  } else if (data.validDropTarget && hover) {
    borderColor = "var(--color-accent)";
    outline = "2px dashed color-mix(in oklab, var(--color-accent) 80%, transparent)";
  } else if (data.pendingPlacementValid) {
    borderColor = "var(--color-accent)";
    outline = "1px dashed color-mix(in oklab, var(--color-accent) 70%, transparent)";
  } else if (data.peerSource) {
    borderColor = "var(--color-accent)";
  } else if (data.peerTargetCandidate) {
    borderColor = "color-mix(in oklab, var(--color-accent) 60%, transparent)";
  }

  return (
    <div
      data-testid={`arch-node-structural-${data.type}`}
      data-arch-node-id={id}
      className="motion-micro"
      style={{
        width: 280,
        minHeight: 84,
        background: "var(--color-background-secondary)",
        border: `1px solid ${borderColor}`,
        outline,
        outlineOffset: 2,
        opacity: data.dimmed ? 0.35 : 1,
        borderRadius: 14,
        boxShadow: data.selected
          ? `0 0 0 2px color-mix(in oklab, var(--color-accent) 55%, transparent), 0 12px 24px -16px rgba(0,0,0,0.55)`
          : "0 1px 0 rgba(255,255,255,0.04) inset, 0 4px 14px -10px rgba(0,0,0,0.5)",
        padding: "12px 14px",
        cursor: data.pendingPlacementValid || data.peerTargetCandidate ? "copy" : "pointer",
        position: "relative",
      }}
      onDragOver={(e) => {
        if (data.validDropTarget) {
          e.preventDefault();
          e.dataTransfer.dropEffect = "copy";
          if (!hover) setHover(true);
        }
      }}
      onDragLeave={() => setHover(false)}
      onDrop={(e) => {
        if (!data.validDropTarget) return;
        e.preventDefault();
        e.stopPropagation();
        const raw = e.dataTransfer.getData("application/arch-payload");
        setHover(false);
        if (raw && data.onDrop) {
          try {
            data.onDrop(id, JSON.parse(raw));
          } catch {
            // ignore malformed payload
          }
        }
      }}
      onContextMenu={(e) => {
        if (data.onContextMenu) {
          e.preventDefault();
          e.stopPropagation();
          data.onContextMenu(id, e.clientX, e.clientY);
        }
      }}
    >
      <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div
            className="font-mono"
            style={{
              fontSize: 10,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: meta.color,
              marginBottom: 4,
              fontWeight: 600,
            }}
          >
            {meta.label}
          </div>
          <div
            className="truncate"
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: "var(--color-text-primary)",
              letterSpacing: "-0.01em",
            }}
            title={data.name}
          >
            {data.name}
          </div>
          {(address || visibility) && (
            <div className="flex items-center gap-2 mt-1.5" data-testid={`arch-node-address-${id}`}>
              {visibility && (
                <span
                  className="font-mono"
                  style={{
                    fontSize: 9.5,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    fontWeight: 600,
                    padding: "1px 6px",
                    borderRadius: 999,
                    // Public and private are the distinction the whole tab exists to
                    // make legible, so they get colour rather than another grey label.
                    color:
                      visibility === "public"
                        ? "var(--color-warning, #d97706)"
                        : "var(--color-success, #0f766e)",
                    background:
                      visibility === "public"
                        ? "color-mix(in oklab, var(--color-warning, #d97706) 15%, transparent)"
                        : "color-mix(in oklab, var(--color-success, #0f766e) 15%, transparent)",
                  }}
                >
                  {visibility}
                </span>
              )}
              {address && (
                <span
                  className="font-mono truncate"
                  style={{ fontSize: 10.5, color: "var(--color-text-secondary)" }}
                  title={address}
                >
                  {address}
                </span>
              )}
            </div>
          )}
          {data.childCount > 0 && (
            <div
              className="font-mono mt-1"
              style={{ fontSize: 10, color: "var(--color-text-tertiary)" }}
            >
              {data.childCount} {data.childCount === 1 ? "child" : "children"}
            </div>
          )}
        </div>
        {data.childCount > 0 && (
          <button
            type="button"
            data-testid={`arch-node-collapse-${id}`}
            onClick={(e) => {
              e.stopPropagation();
              data.onToggleCollapse?.(id);
            }}
            className="motion-micro inline-flex items-center justify-center"
            style={{
              width: 26,
              height: 26,
              borderRadius: 6,
              background: "var(--color-background-tertiary)",
              border: "1px solid var(--color-border-primary)",
              color: "var(--color-text-secondary)",
              cursor: "pointer",
              flexShrink: 0,
            }}
            title={isCollapsed ? "Expand subtree" : "Collapse subtree"}
          >
            {isCollapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
          </button>
        )}
      </div>
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
    </div>
  );
}
